import React from "react";
import clsx from "clsx";

import { ReactComponent as DropdownIcon } from "../../assets/svg/dropdown_icon.svg";
import { ReactComponent as CheckIcon } from "../../assets/svg/check.svg";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import useOnClickOutside from "../../hooks/useOnClickOutside";

import styles from "./Dropdown.module.scss";

type TValue = number;

export interface IOption {
  value: TValue;
  label: string;
}

export type Props = {
  placeholder?: string;
  defaultOption?: IOption | IOption[];
  options: IOption[];
  className?: string;
  multiple?: boolean;
  onChange: (value: IOption[]) => void;
};

const Dropdown: React.FC<Props> = ({
  placeholder = "選んでください",
  defaultOption,
  options,
  className,
  multiple,
  onChange,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<IOption[]>(
    () => {
      if (!defaultOption) return [];
      if (Array.isArray(defaultOption)) return [...defaultOption];
      return [defaultOption];
    },
  );

  const handleSelect = (option: IOption) => {
    if (multiple) {
      const isExisted = selectedOptions.some(
        selectedOption => selectedOption.value === option.value,
      );
      if (isExisted) {
        setSelectedOptions(prevState =>
          prevState.filter(item => item.value !== option.value),
        );
      } else {
        setSelectedOptions(prevState => [...prevState, option]);
      }
    } else {
      setSelectedOptions([option]);
      setOpen(false);
    }
  };

  const handleClickOutside = () => setOpen(false);
  useOnClickOutside(ref, handleClickOutside);

  useDidMountEffect(() => {
    onChange(selectedOptions);
  }, [selectedOptions]);

  return (
    <div
      ref={ref}
      className={clsx(
        styles.wrapper,
        !options.length && styles.disabled,
        className,
      )}
    >
      <div
        className={clsx(styles.currentOption)}
        onClick={() => setOpen(prevState => !prevState)}
      >
        <span>
          {selectedOptions.length
            ? selectedOptions.map(option => option.label).join(", ")
            : placeholder}
        </span>
        <DropdownIcon />
      </div>
      {!!options.length && open && (
        <div className={styles.dropdown}>
          {options.map(option => {
            const isSelected = selectedOptions.some(
              selectedOption => selectedOption.value === option.value,
            );
            return (
              <p
                className={clsx(isSelected && styles.checked)}
                key={option.value}
                onClick={() => handleSelect(option)}
              >
                {option.label}
                {isSelected && <CheckIcon />}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
